import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Box,
  Typography,
  CircularProgress,
  Avatar,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../../../../theme";
import { PersonAdd, CloudUpload } from "@mui/icons-material";

interface SpeakerFormData {
  name: string;
  role: string;
  email: string;
  phone_number: string;
  image: File | null;
}

interface AddSpeakerDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: SpeakerFormData) => Promise<void>;
  accentColor?: string;
}

const emptySpeakerForm: SpeakerFormData = {
  name: "",
  role: "",
  email: "",
  phone_number: "",
  image: null,
};

export default function AddSpeakerDialog({
  open,
  onClose,
  onSubmit,
  accentColor = "#1976D2",
}: AddSpeakerDialogProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [speakerForm, setSpeakerForm] =
    useState<SpeakerFormData>(emptySpeakerForm);
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleFormChange = (field: keyof SpeakerFormData, value: string) => {
    setSpeakerForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSpeakerForm((prev) => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(speakerForm);
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSpeakerForm(emptySpeakerForm);
    setImagePreview(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add New Speaker</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Image Upload */}
          <Box sx={{ textAlign: "center" }}>
            <input
              type="file"
              accept="image/*"
              id="speaker-image-upload"
              hidden
              onChange={handleImageChange}
            />
            <label htmlFor="speaker-image-upload">
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  mx: "auto",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  border: "3px dashed",
                  borderColor: imagePreview ? "transparent" : "divider",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: accentColor,
                    "& .upload-overlay": {
                      opacity: 1,
                    },
                  },
                }}
              >
                {imagePreview ? (
                  <Avatar
                    src={imagePreview}
                    sx={{ width: "100%", height: "100%" }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: colors.primary[800],
                    }}
                  >
                    <CloudUpload sx={{ fontSize: 32, color: "#bdbdbd" }} />
                  </Box>
                )}
                <Box
                  className="upload-overlay"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "rgba(0,0,0,0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0,
                    transition: "opacity 0.2s ease",
                  }}
                >
                  <CloudUpload sx={{ color: "#fff" }} />
                </Box>
              </Box>
            </label>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", mt: 1, display: "block" }}
            >
              Click to upload speaker image
            </Typography>
          </Box>

          <TextField
            label="Name"
            required
            fullWidth
            value={speakerForm.name}
            onChange={(e) => handleFormChange("name", e.target.value)}
            variant="outlined"
          />
          <TextField
            label="Role / Title"
            fullWidth
            value={speakerForm.role}
            onChange={(e) => handleFormChange("role", e.target.value)}
            variant="outlined"
            placeholder="e.g., CEO, Keynote Speaker"
          />
          <TextField
            label="Email"
            fullWidth
            type="email"
            value={speakerForm.email}
            onChange={(e) => handleFormChange("email", e.target.value)}
            variant="outlined"
          />
          <TextField
            label="Phone Number"
            fullWidth
            value={speakerForm.phone_number}
            onChange={(e) => handleFormChange("phone_number", e.target.value)}
            variant="outlined"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !speakerForm.name.trim()}
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PersonAdd />
            )
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            bgcolor: accentColor,
            "&:hover": { bgcolor: accentColor, opacity: 0.9 },
          }}
        >
          {submitting ? "Adding..." : "Add Speaker"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
